import { Component, Inject, Input, NgZone, OnInit, QueryList, ViewChildren } from '@angular/core';
import { BubbleDataPoint, ChartConfiguration, PointStyle, ScriptableContext } from 'chart.js';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';
import { DateRangeSelectorService } from 'src/app/reusables/date-range-selector/date-range-selector.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { TimeInterval } from 'src/shared/models/module-manager/module-manager-settings.models';
import { CxoneIntervalSelectorComponent } from '../../toolbar-items/cxone-interval-selector/cxone-interval-selector.component';
import { CxoneKPITrendModuleData, KPITrendModuleDisplay } from './kpi-trend-coaching-events.models';
import { CxoneKpiTrendService } from '../cxone-kpi-trend/cxone-kpi-trend.service';
import { NumericType, TimeType } from "src/shared/models/metric";
import { KpiTrendCoachingHeaderComponent } from './kpi-trend-coaching-header/kpi-trend-coaching-header.component';
import { AnyObject } from 'chart.js/dist/types/basic';
import { SharedDataService } from './kpi-trend-coaching-header/kpi-trend-coaching-header.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'kpi-trend-coaching-events',
    templateUrl: './kpi-trend-coaching-events.component.html',
    styleUrls: ['./kpi-trend-coaching-events.component.scss']
})
export class KpiTrendCoachingEventsComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    @ViewChildren(BaseChartDirective) chartObject: QueryList<BaseChartDirective>;
    isFirstLoad: boolean;
    noData = false;
    isNoMetric = false;
    intervalRangeTooWide = false;
    isCXOne = false;
    dataLoadSuccess = false;
    preventLoadingModal = true;
    public moduleDisplay: KPITrendModuleDisplay = KPITrendModuleDisplay.Detailed;
    updateWidth = false;
    public chartConfig: ChartConfiguration;
    public metric: CxoneKPITrendModuleData;
    public chartData: Map<string, string>;
    public metricName: string;
    currentValue: string;
    trendDisplayValue: string;
    trendDifference: number;
    previousLabel: string;
    previousValue: string;
    public arrowDirection = true;
    public dynamicColor = '';
    intervalSelected: { id: number; order: number; name: string };
    unit: string;
    numericType: number;
    timeType: number;
    trendShowDirection: boolean;
    receivedAgents: any;
    private subscription: Subscription;

    private kpiTrendContext = {
        viewStateLoaded: () => this.moduleDisplay === KPITrendModuleDisplay.Detailed,
        onIntervalChange: () => this.intervalSelectionChanged.bind(this),
        settingsSelectedInterval: null,
    };

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private service: CxoneKpiTrendService,
        private zone: NgZone,
        private config: CvConfigurationService,
        private dateRangeSelectorService: DateRangeSelectorService,
        public ShardDataservice: SharedDataService
    ) {
        this.isCXOne = this.config.isCxOne;
        this.subscription = this.ShardDataservice.data$.subscribe(data => {
            this.receivedAgents = data;
        });
    }

    async ngOnInit(): Promise<void> {
        this.isFirstLoad = true;
        this.moduleManager.setOptions({
            name: 'KPI Trend',
            widgetMode: false,
            onSettingsApplied: settings => this.zone.run(() => {
                this.kpiTrendContext.settingsSelectedInterval = settings.intervalType;
                this.loadData(false);
            }),
            hasRefresh: true,
            onRefresh: isAutoRefresh => this.zone.run(() => this.loadData(isAutoRefresh)),
            canRefresh: () => !this.moduleManager.showLoader,
            onExport: (format) => this.zone.run(() => this.service.LoadDataForExport(this.moduleKey, format)),
        });

        this.moduleManager.addState({
            name: 'setInterval',
            autoLoad: true,
            getStateData: () => this.intervalSelected,
            onStateLoaded: (stateData) => {
                this.intervalSelected = stateData != null ? stateData : '';
            },
        });
        await this.cvLocalizationService.GetSection("KPI_Trend_Module");
        // assures menu items are set on the first load
        this.resetMenuItems();
        this.noData = false;
        this.moduleManager.addToolbarItem(KpiTrendCoachingHeaderComponent, this.kpiTrendContext);
    }

    /**
     * Resets the module menu items
     */
    resetMenuItems() {
        this.moduleManager.resetMenu();
        this.addMenuItems();
    }

    async addMenuItems() {
        const displayItems = [{
            name: this.cvLocalizationService.getCachedText("KPI_Trend_Module", "Compact"),
            icon: '',
            click: () => {
                this.zone.run(() => {
                    this.changeAndSaveModuleDisplay(KPITrendModuleDisplay.Compact);
                });
            }
        },
        {
            name: this.cvLocalizationService.getCachedText("KPI_Trend_Module", "Detailed"),
            icon: '',
            click: () => {
                this.zone.run(() => {
                    this.changeAndSaveModuleDisplay(KPITrendModuleDisplay.Detailed);
                });
            }
        }];
        this.moduleManager.addMenuItem({
            name: 'Widget Display',
            icon: 'chartType',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.dataLoadSuccess,
            subItems: displayItems,
            readOnlyEnabled: this.isCXOne
        } as ModuleManagerSubMenu);
    }

    changeAndSaveModuleDisplay(moduleDisplay: KPITrendModuleDisplay) {
        if (this.moduleDisplay === moduleDisplay) // going to the same display view as currently shown
            return;
        this.moduleDisplay = moduleDisplay;
        this.updateWidth = true;
        this.setDisplay();
        this.loadData(false);
        this.resetMenuItems();
        this.moduleManager.saveState('viewModeKPITrend');
        this.moduleManager.requestPublishNeeded();
    }

    async loadData(isAutoRefresh: boolean): Promise<any> {
        // call the AutoRefreshReady only when the loadData call
        // is successful and the data returned without any error.
        // if there's any exception occured then call handleError
        // method (check kpi.component).
        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;
        this.moduleManager.preventModuleMapUpdate();
        try {
            if (this.moduleManager.getModuleSettings().selectedMetrics.length > 0) {
                this.metric = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            }
            if (this.metric == null) {
                // this case is when there is no metric selected.
                this.moduleManager.showLoader = false;
                this.isNoMetric = true;
                return;
            }
            else if (this.metric.Error === 1) {
                this.noData = true;
                this.intervalRangeTooWide = false;
            } else if (this.metric.Error === 2) {
                this.noData = false;
                this.intervalRangeTooWide = true;
            } else {
                this.noData = false;
                this.intervalRangeTooWide = false;
                if (!(this.metric.KpiTrendData[0].ChartData instanceof Map))
                    this.chartData = new Map(Object.entries(this.metric.KpiTrendData[0].ChartData));
                this.moduleManager.setAutoRefreshReady();
                if (this.chartData) {
                    const kpiData = this.metric.KpiTrendData[0];
                    this.metricName = kpiData.MetricName;
                    this.numericType = kpiData.NumericType;
                    this.timeType = kpiData.TimeType;
                    this.currentValue = kpiData.DisplayValue;
                    this.trendDisplayValue = kpiData.Trend?.DisplayValue !== null ? `${kpiData.Trend?.DisplayValue}%` : null;
                    this.trendShowDirection = kpiData.Trend?.ShowDirection;
                    this.trendDifference = kpiData.Trend?.Difference;
                    this.previousLabel = kpiData.PreviousLabel;
                    this.previousValue = kpiData.PreviousValue;
                    this.getUnit(this.numericType);
                    //Select dynamic color for arrow and value
                    this.dynamicColor = kpiData.Trend?.Color;
                    //Set arrow direction
                    this.arrowDirection = kpiData.Trend?.Difference > 0 ? true : false;
                    this.dataLoadSuccess = true;
                }
                this.updateChartData();
            }
        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.moduleManager.removeModuleMapPrevention();
        }
        this.isFirstLoad = false;
        this.moduleManager.showLoader = false;
    }

    formatValue(value: any, numericType: number, timeDisplayValue: string): string {
        if (value !== null) {
            value = parseFloat(value.toFixed(2)).toLocaleString();
            if (numericType === NumericType.Currency) {
                return '$' + value;
            } else if (numericType === NumericType.Percent) {
                return value + '%';
            } else if (numericType === NumericType.Time) {
                return timeDisplayValue;
            } else {
                return value; // No specific formatting for other types
            }
        } else {
            return 'N/A';
        }
    }

    getUnit(numericType: number) {
        if (numericType === NumericType.Currency) {
            this.unit = '$';
        } else if (numericType === NumericType.Percent) {
            this.unit = '%';
        } else if (numericType === NumericType.Time) {
            this.unit = 'Time';
        } else {
            this.unit = '';
        }
    }

    setDisplay() {
        this.moduleManager.container.toolbarHidden = this.moduleDisplay === KPITrendModuleDisplay.Compact;
        if (this.moduleDisplay === KPITrendModuleDisplay.Compact) {
            this.moduleManager.setSize(8, 6, false);
            this.moduleManager.setMinSize(8, 6);
            this.moduleManager.setMaxSize(32, 12);
        }
        else {
            this.moduleManager.setSize(12, 7, false);
            this.moduleManager.setMinSize(12, 7);
            this.moduleManager.setMaxSize(32, 12);
        }
    }

    async intervalSelectionChanged(intervalSelected): Promise<void> {
        this.intervalSelected = intervalSelected;
        await this.dateRangeSelectorService.saveIntervalSettings(
            `${this.moduleKey}_temp`,
            1,
            intervalSelected.id
        );
        await this.moduleManager.container.applySettings();
        this.moduleManager.loadSettings(false);
    }

    tooltipExternalHandle (context) {
        let tooltipEl = document.getElementById('chartjs-tooltip');
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.innerHTML = '<table></table>';
            document.body.appendChild(tooltipEl);
        }
        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
        }
        if (tooltipModel.body) {
            const dataFromCurrentElement = tooltipModel.dataPoints[0] as any;
            const metricName = this.moduleManager.getModuleSettings().selectedMetrics[0].Name;
            const displayValue = 1;
            const metricval = 1;
            let total: number;
            if (Array.isArray((dataFromCurrentElement.dataset as any).data)) {
                total = (dataFromCurrentElement.dataset as any).data.reduce((acc, val) => acc + parseFloat(val), 0);
            } else {
                total = (dataFromCurrentElement.dataset as any).data as number;
            }
            const percentage = 80 + '%';
            const boldMetricValue = `<span style="font-weight: bold">${displayValue}</span>`;
            const boldPercentage = `<span style="font-weight: bold">${percentage}</span>`;
            const innerHtml = `<div class="kpi-trend-coaching-events-tooltip-container">
                        <div class="kpi-trend-coaching-events-tooltip-content">
                            <div style="display: flex;">
                                <div class="kpi-trend-coaching-events-point"></div>
                                <div class="kpi-trend-coaching-events-bold-text">dasdad</div>
                            </div>
                            <div class="kpi-trend-coaching-events-tooltip-text">a: aa</div>
                            <div class="kpi-trend-coaching-events-tooltip-text">b: bb</div>
                        </div>
                    </div>`;
            tooltipEl.querySelector('table').innerHTML = innerHtml;
        }
        const position = context.chart.canvas.getBoundingClientRect();
        tooltipEl.style.opacity = '1';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - tooltipEl.clientHeight + 'px';
        tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
        tooltipEl.style.padding = '5px 5px';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.zIndex = '1000';
    }

    getChartConfig(): ChartConfiguration {
        return {
            type: "line",
            data: {
                datasets: [],
                labels: []
            },
            options: {
                datasets: {
                    line: {
                        spanGaps: true,
                        pointRadius: 0
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    legend: {
                        display: true,
                    },
                    tooltip: {
                        enabled: true,
                        filter: tooltipItem => tooltipItem.datasetIndex == 2,
                        mode: 'index',
                        intersect: true,
                        caretPadding: 10,
                        displayColors: false,
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        borderWidth: 1,
                        borderColor: 'rgba(208, 210, 211, 1)',
                        titleFont: {
                            family: 'Inter',
                            size: 11,
                            weight: 'normal',
                        },
                        titleColor: '#8C95A5',
                        bodyFont: {
                            family: 'Open Sans, sans-serif',
                            size: 13,
                            weight: 'normal',
                        },
                        bodyColor: '#2E2E2E',
                        callbacks: {
                            title: (item) => moment((item[0].raw as any).x.toString()).format('D MMM'),
                            label: (item) => `${item.parsed.y.toLocaleString()} ${this.unit}`,
                        }
                    },
                },
                scales: {
                    x: {
                        distribution: 'series',
                        scaleLabel: {
                            display: false,
                        },
                        grid: {
                            display: false
                        },
                        ticks: {
                            autoSkip: true,
                            source: 'labels',
                            maxTicksLimit: 5,
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: this.unit,
                            font: {
                                family: 'Open Sans, sans-serif',
                                size: 12,
                                weight: '400',
                            },
                        },
                        grid: {
                            display: true,
                            color: 'rgba(232, 232, 232, 1)'
                        },
                        border: {
                            display: true,
                            dash: [4, 4],
                        },
                        suggestedMax: 10,
                        suggestedMin: 0,
                    }
                },
                layout: {
                    padding: {
                        bottom: 11.37,
                        right: 10,
                        left: 2.4
                    }
                }
            },
        } as ChartConfiguration;
    }

    generateDataArray(): BubbleDataPoint[] {
        const dataArray: BubbleDataPoint[] = [];
        const startDate = new Date('2023-11-21T22:00:00.000Z');
        const endDate = new Date('2024-02-18T22:00:00.000Z');
        const millisecondsPerDay = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
    
        // Generate data for each day
        for (let currentDate = startDate; currentDate <= endDate; currentDate.setDate(currentDate.getDate() + 1)) {
            const randomY = Math.floor(Math.random() * (5000 - 1000 + 1)) + 1000; // Random value for y between 1000 and 5000
            const dataPoint: BubbleDataPoint = {
                x: currentDate.getTime(), // Convert date to Unix timestamp
                y: randomY, // Assign randomY directly as a number
                r: 5 // Sample radius value, adjust as needed
            };
            dataArray.push(dataPoint);
        }
    
        return dataArray;
    }
    

    updateChartData() {
        if (!this.metric)
            return;

        this.chartConfig = this.getChartConfig();
        const intervalType = this.moduleManager.getModuleSettings().intervalType;
        const isTimeIntervalShortDuration = intervalType === TimeInterval.FifteenMinute || intervalType === TimeInterval.HalfHour || intervalType === TimeInterval.Hour;

        const data: null | { x: moment.Moment, y: string }[] = [];
        const labels: string[] = [];

        if (this.numericType === NumericType.Time) {
            this.chartConfig.options.scales.y.ticks = {};
            if (this.timeType === TimeType.Time) {
                this.chartData.forEach((value, key) => {
                    let totalSeconds = null;
                    if (value !== null) {
                        // Convert time duration strings (e.g., "00:01:08") to seconds
                        const [hours, minutes, seconds] = value.split(':').map(Number);
                        totalSeconds = hours * 3600 + minutes * 60 + seconds;
                    }
                    data.push(totalSeconds);
                    this.formatLabel(labels, key, isTimeIntervalShortDuration);
                });
                this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationSeconds(value);
                this.chartConfig.options.plugins.tooltip.callbacks.label = (tooltipItem) => {
                    const value = tooltipItem.raw;
                    return this.formatDurationSeconds(value);
                };
            } else {
                if (this.timeType === TimeType.Second) {
                    this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationSeconds(value);
                }
                if (this.timeType === TimeType.Minute) {
                    this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationMinutes(value);
                }
                if (this.timeType === TimeType.Hour) {
                    this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationHours(value);
                }
                this.chartConfig.options.plugins.tooltip.callbacks.label = (tooltipItem: any) => tooltipItem.raw.toLocaleString();
            }
            this.chartConfig.options.plugins.tooltip.callbacks.title = (tooltipItem: any) => tooltipItem[0].label;
        } else {
            this.chartData.forEach((value, key) => {
                data.push({ x: moment(key, "YYYY-MM-DD"), y: value });
                this.formatLabel(labels, key, isTimeIntervalShortDuration);
            });
            if (isTimeIntervalShortDuration) {
                this.chartConfig.options.plugins.tooltip.callbacks.title = (tooltipItem) => {
                    const value = tooltipItem[0].label;
                    return value;
                };
            }
            this.chartConfig.options.plugins.tooltip.callbacks.label = (tooltipItem: any) => {
                return this.formatValue(tooltipItem.parsed.y, this.numericType, "");
            };
        }
        const data2 = this.generateDataArray();
        const data3 = this.generateDataArray();
        const data1 = this.generateDataArray();
        const indexesOfCoaching = [0, 10, 12, 30];
        var icon = new Image();
        let coachingSessionIcon = '<svg width="26" height="23" viewBox="0 0 26 23" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.8225 2.34231L12.8226 2.34232L12.8225 2.34231ZM13.0531 2.45632L22.1228 6.9916L21.8988 7.10363C21.8841 7.11061 21.8695 7.11788 21.8552 7.12545L18.894 8.60615L13.5756 5.94665C12.9985 5.65809 12.2968 5.89201 12.0083 6.46913C11.7197 7.04624 11.9536 7.748 12.5307 8.03656L16.2819 9.91234L13.0531 11.5269L4.25111 7.12545C4.23674 7.11789 4.22219 7.11061 4.20747 7.10363L3.98344 6.9916L13.0531 2.45632ZM2.53949 8.88195L0.84888 8.03656C0.453119 7.83866 0.203125 7.43412 0.203125 6.9916C0.203125 6.54909 0.453119 6.14455 0.84888 5.94665L12.1128 0.314163C12.1214 0.309835 12.1314 0.304737 12.1426 0.299008C12.2516 0.243386 12.4772 0.128274 12.73 0.0808326C12.9436 0.0407641 13.1627 0.0407641 13.3762 0.0808326C13.6291 0.128274 13.8546 0.243389 13.9636 0.29901C13.9749 0.304738 13.9848 0.309836 13.9935 0.314163L25.2574 5.94665C25.6531 6.14455 25.9031 6.54909 25.9031 6.9916C25.9031 7.43412 25.6531 7.83866 25.2574 8.03656L23.5668 8.88195V16.112C23.5668 16.1418 23.5669 16.1724 23.567 16.2038C23.5683 16.5375 23.57 16.9568 23.4312 17.3462C23.3114 17.6825 23.1161 17.9869 22.8603 18.2359C22.5642 18.5243 22.1824 18.6975 21.8785 18.8354C21.8499 18.8483 21.822 18.861 21.795 18.8734L14.3186 22.3004C14.2992 22.3093 14.2792 22.3185 14.2588 22.328C14.0371 22.4304 13.758 22.5593 13.4528 22.6123C13.1883 22.6582 12.9179 22.6582 12.6535 22.6123C12.3483 22.5593 12.0692 22.4304 11.8475 22.328C11.827 22.3185 11.8071 22.3093 11.7876 22.3004L4.31127 18.8734L4.798 17.8113L4.31126 18.8734C4.28421 18.861 4.25633 18.8483 4.22777 18.8354C3.9239 18.6975 3.54209 18.5243 3.24595 18.2359C2.99018 17.9869 2.79487 17.6825 2.67504 17.3462C2.5363 16.9568 2.53794 16.5375 2.53925 16.2038C2.53937 16.1724 2.53949 16.1418 2.53949 16.112V8.88195ZM4.87585 10.0502V16.112C4.87585 16.3438 4.8764 16.4635 4.88194 16.55C4.88211 16.5527 4.88228 16.5552 4.88245 16.5576C4.88458 16.5588 4.88681 16.56 4.88915 16.5613C4.96551 16.6023 5.07404 16.6527 5.28473 16.7493L12.7611 20.1763C12.9144 20.2466 12.9915 20.2815 13.0487 20.3044C13.0502 20.305 13.0517 20.3056 13.0531 20.3061C13.0545 20.3056 13.056 20.305 13.0576 20.3044C13.1147 20.2815 13.1918 20.2466 13.3452 20.1763L20.8215 16.7493C21.0322 16.6527 21.1407 16.6023 21.2171 16.5613C21.2194 16.56 21.2217 16.5588 21.2238 16.5576C21.224 16.5552 21.2241 16.5527 21.2243 16.55C21.2298 16.4635 21.2304 16.3438 21.2304 16.112V10.0502L20.0622 10.6344V14.0014C20.0622 14.6466 19.5392 15.1697 18.894 15.1697C18.2489 15.1697 17.7259 14.6466 17.7259 14.0014V11.8027L13.9935 13.669C13.9848 13.6734 13.9749 13.6785 13.9636 13.6842C13.8546 13.7398 13.6291 13.8549 13.3762 13.9024C13.1627 13.9424 12.9436 13.9424 12.73 13.9024C12.4772 13.8549 12.2516 13.7398 12.1426 13.6842C12.1314 13.6785 12.1214 13.6734 12.1128 13.669L4.87585 10.0502ZM12.8225 11.6409C12.8223 11.641 12.8223 11.641 12.8226 11.6409L12.8225 11.6409ZM13.2837 11.6409C13.2839 11.641 13.2839 11.641 13.2837 11.6409L13.2837 11.6409ZM13.2837 2.34232L13.2837 2.34231L13.2837 2.34232Z" fill="#8F0AFF"/></svg>';
        const iconLink = '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg>'
        icon.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(coachingSessionIcon);
        
        const iconDataURL = iconLink;
        this.chartConfig.data.datasets = [{
            data: data1,
            borderColor: '#8F0AFF',
            fill: false,
            backgroundColor: '#8F0AFF',
            tension: 0.1,
            pointBackgroundColor: '#8F0AFF',
            pointHoverBorderColor: '#8F0AFF',
            pointHoverBorderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
            label: 'Agent 1',
        }, {
            data: data2,
            borderColor: '#AAC331',
            fill: false,
            backgroundColor: '#AAC331',
            tension: 0.1,
            pointBackgroundColor: '#AAC331',
            pointHoverBorderColor: '#AAC331',
            pointHoverBorderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0, 
            label: 'Agent 2',
        },
        {
            data: data3,
            borderColor: '#EFA716',
            fill: false,
            backgroundColor: '#EFA716',
            tension: 0.1,
            pointBackgroundColor: '#EFA716',
            pointHoverBorderColor: '#EFA716',
            pointHoverBorderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
            label: 'Agent 3',
        },
        {
            data: data1.map((v, i) => indexesOfCoaching.includes(i) ? {x: v.x, y: v.y+ 0.1*v.y, r: v.r, customStyle: iconDataURL} : {x: v.x, y: null, r: v.r}),
            fill: false,
            pointStyle: icon,
            pointRadius: 5,
            pointHoverRadius: 5,
            showLine: false,
            borderColor: 'rgba(0,0,0,0)',
            backgroundColor: 'rgba(0,0,0,0)',
            label: '',
        },
        {
            data: data1.map((v, i) => indexesOfCoaching.includes(i) ? {x: v.x, y: v.y, r: v.r} : {x: v.x, y: null, r: v.r}),
            fill: false,
            pointStyle: 'circle',
            pointBackgroundColor: '#8F0AFF',
            pointHoverBorderColor: '#8F0AFF',
            pointRadius: 5,
            pointHoverRadius: 5,
            pointHoverBorderWidth: 5,
            showLine: false,
            borderColor: 'rgba(0,0,0,0)',
            backgroundColor: 'rgba(0,0,0,0)',
            label: '',
        }];
        this.chartConfig.data.labels = labels;
    }

    // Function to convert seconds to "hh:mm:ss" format
    formatDurationSeconds(value) {
        const hours = Math.floor(value / 3600);
        const minutes = Math.floor((value % 3600) / 60);
        const seconds = value % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    // Function to convert minutes to "hh:mm:ss" format
    formatDurationMinutes(value) {
        const hours = Math.floor(value / 60);
        const remainingMinutes = value % 60;
        const totalSeconds = remainingMinutes * 60;
        const seconds = totalSeconds % 60;
        return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    // Function to convert hours to "hh:mm:ss" format
    formatDurationHours(value) {
        const totalSeconds = value * 3600;
        const hours = Math.floor(totalSeconds / 3600);
        const remainingSeconds = totalSeconds % 3600;
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    formatLabel(labels, value, isTimeIntervalShortDuration) {
        if (isTimeIntervalShortDuration) {
            labels.push(moment(value).utc().format("h:mm A"));
        } else {
            labels.push(moment(value, "YYYY-MM-DD").format("MMM DD"));
        }
    }

}

